import { useCallback, useEffect, useState } from 'react';
import { Content, Header, Page } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { Box, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { AccessRequest, TeamRow, portalAccessApiRef } from './api';
import { canRequest, teamLabel } from './rules';

const STATUS: Record<string, string> = { requested: '대기', processing: '처리 중', approved: '승인', rejected: '거절', cancelled: '취소' };

/** 권한 신청 (로그인 사용자 전원). team 을 고르고 사유를 적어 제출한다 — 이미 소속·대기 중인 team 은 비활성 */
export const AccessRequestPage = () => {
  const api = useApi(portalAccessApiRef);
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [mine, setMine] = useState<AccessRequest[]>([]);
  const [team, setTeam] = useState('');
  const [reason, setReason] = useState('');
  const [leaving, setLeaving] = useState<{ team: string; reason: string }>();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.teams(), api.myRequests()]).then(([t, m]) => {
      setTeams(t);
      setMine(m);
    }, e => setError((e as Error).message));
  }, [api]);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await fn();
      setNotice(done);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      load();
    }
  };
  const selected = teams.find(t => t.team === team);
  const memberTeams = teams.filter(t => t.member);

  return (
    <Page themeId="tool">
      <Header title="권한 신청" subtitle="팀 권한을 신청하면 승인자에게 Slack 으로 알림이 갑니다" />
      <Content>
        {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}
        {notice && <Alert severity="success" style={{ marginBottom: 16 }}>{notice}</Alert>}
        <Paper style={{ padding: 16, marginBottom: 16 }}>
          <Typography variant="h6">새 신청</Typography>
          <Table size="small" aria-label="team 목록">
            <TableHead><TableRow><TableCell /><TableCell>team</TableCell><TableCell>상태</TableCell></TableRow></TableHead>
            <TableBody>
              {teams.map(t => (
                <TableRow key={t.team}>
                  <TableCell padding="checkbox">
                    <input type="radio" name="team" aria-label={t.team} checked={team === t.team} disabled={!canRequest(t)} onChange={() => setTeam(t.team)} />
                  </TableCell>
                  <TableCell>{t.team}</TableCell>
                  <TableCell>{teamLabel(t)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box mt={2} display="flex" style={{ gap: 12 }}>
            <TextField label="신청 사유" inputProps={{ 'aria-label': '신청 사유' }} fullWidth size="small" variant="outlined" value={reason} onChange={e => setReason(e.target.value)} />
            <Button
              color="primary" variant="contained" disabled={busy || !selected || !canRequest(selected) || !reason.trim()}
              onClick={() => act(() => api.createRequest(team, reason.trim()).then(() => { setReason(''); setTeam(''); }), '신청했습니다. 승인되면 최대 약 6분 뒤(새로고침 후) 반영됩니다.')}
            >
              신청
            </Button>
          </Box>
        </Paper>

        <Paper style={{ padding: 16, marginBottom: 16 }}>
          <Typography variant="h6">내 신청</Typography>
          <Table size="small" aria-label="내 신청">
            <TableHead><TableRow><TableCell>team</TableCell><TableCell>사유</TableCell><TableCell>상태</TableCell><TableCell>비고</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {mine.map(r => (
                <TableRow key={r.id}>
                  <TableCell>{r.team}</TableCell>
                  <TableCell>{r.reason}</TableCell>
                  <TableCell>{STATUS[r.status] ?? r.status}</TableCell>
                  <TableCell>{r.decidedReason ?? r.lastError ?? ''}</TableCell>
                  <TableCell>
                    {r.status === 'requested' && <Button size="small" disabled={busy} onClick={() => act(() => api.cancelRequest(r.id), '취소했습니다.')}>취소</Button>}
                  </TableCell>
                </TableRow>
              ))}
              {!mine.length && <TableRow><TableCell colSpan={5}>신청 내역이 없습니다</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Paper>

        <Paper style={{ padding: 16 }}>
          <Typography variant="h6">내 소속 team</Typography>
          {!memberTeams.length && <Typography variant="body2">소속된 team 이 없습니다</Typography>}
          {memberTeams.map(t => (
            <Box key={t.team} display="flex" alignItems="center" style={{ gap: 12 }} mt={1}>
              <Typography>{t.team}</Typography>
              {leaving?.team === t.team ? (
                <>
                  <TextField size="small" variant="outlined" label="탈퇴 사유" inputProps={{ 'aria-label': '탈퇴 사유' }} value={leaving.reason} onChange={e => setLeaving({ team: t.team, reason: e.target.value })} />
                  <Button size="small" color="secondary" variant="contained" disabled={busy || !leaving.reason.trim()}
                    onClick={() => act(() => api.leave(t.team, leaving.reason.trim()).then(() => setLeaving(undefined)), '탈퇴했습니다. 최대 약 6분 뒤(새로고침 후) 반영됩니다.')}>
                    탈퇴 확정
                  </Button>
                  <Button size="small" onClick={() => setLeaving(undefined)}>닫기</Button>
                </>
              ) : (
                <Button size="small" onClick={() => setLeaving({ team: t.team, reason: '' })}>자진 탈퇴</Button>
              )}
            </Box>
          ))}
        </Paper>
      </Content>
    </Page>
  );
};
